import { defineStore } from "pinia";
import AuthApiService from "@/core/services/authApiService";
import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import {
  getLocalStorageService,
  setLocalStorageService,
} from "@/core/helpers/utility";
import { useEntityStore } from "./Entity";
import ConfirmEmail from "@/views/Auth/confirmEmail.vue";
export const useAuthStore = defineStore("auth", {
  state: () => ({
    errors: {},
    isAuthenticated: !!JwtService.getToken(),
    stateSubEntity: [],
    accessToken: localStorage.getItem("id_token") || null,
    refreshToken: localStorage.getItem("id_refresh_token") || null,
    cachedAuthUser: null,
    cachedUserRoles: [],
  }),
  getters: {
    user() {
      return this.cachedAuthUser?.profileInfo || {};
    },
    userRoles() {
      return this.cachedUserRoles;
    },
  },
  actions: {
    initializeCachedAuth() {
      const authUser = localStorage.getItem("auth-user");

      if (!authUser) {
        this.cachedAuthUser = null;
        this.cachedUserRoles = [];
        return;
      }

      try {
        const parsedAuthUser = JSON.parse(authUser);
        this.cachedAuthUser = parsedAuthUser;
        this.cachedUserRoles = parsedAuthUser?.role || [];
      } catch (error) {
        this.cachedAuthUser = null;
        this.cachedUserRoles = [];
      }
    },
    setAuth(authUser) {
      this.isAuthenticated = true;
      this.errors = {};
      JwtService.saveToken(authUser.token);
      JwtService.saveRefreshToken(authUser?.refreshToken ?? "");
      AuthApiService.setAuthorizationHeader();
      this.setUser(authUser);
    },
    setError(error) {
      this.errors = { ...error };
    },
    purgeAuth() {
      let getCurrentView = localStorage.getItem("currentView");
      localStorage.clear();
      if (getCurrentView) {
        localStorage.setItem("currentView", getCurrentView);
      }
      this.isAuthenticated = false;
      this.errors = [];
      this.cachedAuthUser = null;
      this.cachedUserRoles = [];
      // JwtService.destroyToken();
    },
    _confirmEmail(credentials) {
      return AuthApiService.post("V2/account/confirmEmail", credentials)
        .then(({ data }) => {
          return data;
        })
        .catch((response) => {
          this.setError(response?.response?.data);
        });
    },
    _confirmVendor(params) {
      const { id = "", status = "", code = "" } = params ?? {};
      return AuthApiService.put(
        `/v2/Maintenance/VendorEngaged?id=${id}&status=${status}&code=${code}`,
      )
        .then(({ data }) => {
          return data;
        })
        .catch((response) => {
          this.setError(response?.response?.data);
        });
    },
    getRefreshToken() {
      return AuthApiService.post("/account/refreshToken", {
        token: this.accessToken,
        refreshToken: this.refreshToken,
      })
        .then(({ data }) => {
          const newAccessToken = data.token;
          const newRefreshToken = data.refreshToken;
          this.accessToken = newAccessToken;
          this.refreshToken = newRefreshToken;
          window.localStorage.setItem("id_token", newAccessToken);
          window.localStorage.setItem("id_refresh_token", newRefreshToken);
          return newAccessToken;
        })
        .catch((response) => {
          this.setError(response?.response?.data);
        });
    },
    resolverLogin(credentials) {
      // "Account/resolver" master login
      return AuthApiService.post("Account/resolver", credentials)
        .then(({ data }) => {
          this.setAuth(data);

          return data;
        })
        .catch((response) => {
          this.setError(response?.response?.data);
        });
    },
    login(credentials) {
      // "Account/resolver" master login
      return AuthApiService.post("Account/login", credentials)
        .then(({ data }) => {
          this.setAuth(data);

          return data;
        })
        .catch((response) => {
          this.setError(response?.response?.data);
          return Promise.reject(response);
        });
    },
    logout() {
      this.purgeAuth();
    },
    register(credentials) {
      return AuthApiService.post("register", credentials)
        .then(({ data }) => {
          this.setAuth(data);
        })
        .catch(({ response }) => {
          this.setError(response.data);
        });
    },
    async getEntity() {
      return await ApiService.get(`v2/Entity/MyEntity`)
        .then((response) => {
          const entityStore = useEntityStore();
          const result = response.data.data[0];
          entityStore.localEntity = [
            {
              entityType: result.entityType,
              entityId: result.id,
            },
          ];
          setLocalStorageService("sub-entity", [
            {
              entityType: result.entityType,
              entityId: result.id,
            },
          ]);
        })
        .catch(({ error }) => {
          throw error;
        });
    },
    forgotPassword(email) {
      return AuthApiService.post(`Account/ForgetPassword/${email}`)
        .then((response) => {
          this.setError();
          return response.data;
        })
        .catch(({ response }) => {
          this.setError(response.data.message);
          throw response;
        });
    },
    resetPassword(credentials) {
      return AuthApiService.post(`V2/Account/ResetPassword`, credentials)
        .then((response) => {
          this.setError();
          return response.data;
        })
        .catch(({ response }) => {
          this.setError(response.data.message);
          throw response;
        });
    },

    setUser(userDetails) {
      localStorage.setItem("auth-user", JSON.stringify(userDetails));
      localStorage.setItem("userId", JSON.stringify(userDetails.userId));
      this.cachedAuthUser = userDetails;
      this.cachedUserRoles = userDetails?.role || [];
    },
  },
});
